import { Box, BoxProps, Chip, Tab, TabProps, Tabs, TabsProps, Typography } from '@mui/material';
import { IconButton } from '@portals/core/src/components/IconButton/IconButton';
import { theme } from '@portals/core/src/themes/sde/main';
import { ChevronLeft, ChevronRight } from '@portals/icons';
import { getFixedT } from 'i18next';
import React, { useState } from 'react';

import { getStyles } from './Tabpanel.style';

const t = getFixedT(null, 'core-immobilien');

export interface TabItem {
  label: string | React.ReactNode;
  counter?: string | number | null;
  icon?: React.ReactElement;
  content: React.ReactNode;
  tabProps?: Partial<TabProps>;
  tabPanelProps?: Partial<BoxProps>;
}

export interface TabpanelProps {
  ariaLabelTitle: string;
  tabs: TabItem[];
  variant?: 'standard' | 'scrollable' | 'fullWidth';
  scrollButtons?: boolean;
  allowScrollButtonsMobile?: boolean;
  selectionFollowsFocus?: boolean;
  tabsProps?: Partial<TabsProps>;
  tabProps?: Partial<TabProps>;
  tabPanelProps?: Partial<BoxProps>;
}

export const Tabpanel: React.FC<TabpanelProps> = ({
  ariaLabelTitle,
  tabs = [],
  variant = 'scrollable',
  scrollButtons = true,
  allowScrollButtonsMobile = true,
  selectionFollowsFocus = true,
  tabsProps = {},
  tabProps = {},
  tabPanelProps = {},
}) => {
  const styles = getStyles(theme);
  const [activeTab, setActiveTab] = useState(0);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => setActiveTab(newValue);

  const goToPreviousTab = () => {
    setActiveTab((prev) => Math.max(prev - 1, 0));
  };

  const goToNextTab = () => {
    setActiveTab((prev) => Math.min(prev + 1, tabs.length - 1));
  };

  return (
    <Box sx={styles.outerContainer}>
      <Box sx={styles.tabScrollWrapper} className="tabpanel-header">
        <Tabs
          variant={variant}
          scrollButtons={scrollButtons}
          allowScrollButtonsMobile={allowScrollButtonsMobile}
          selectionFollowsFocus={selectionFollowsFocus}
          textColor="inherit"
          value={activeTab}
          onChange={handleChange}
          aria-label={ariaLabelTitle}
          slots={{
            StartScrollButtonIcon: () => {
              return (
                <IconButton aria-label={t('tabpanel.previousTab')} onClick={goToPreviousTab}>
                  <ChevronLeft />
                </IconButton>
              );
            },
            EndScrollButtonIcon: () => {
              return (
                <IconButton aria-label={t('tabpanel.nextTab')} onClick={goToNextTab}>
                  <ChevronRight />
                </IconButton>
              );
            },
          }}
          {...tabsProps}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              icon={tab.icon}
              iconPosition="start"
              label={
                <Box sx={styles.tabHeader}>
                  {typeof tab.label === 'string' ? (
                    <Typography variant="h4" aria-label={`Tab title: ${tab.label}`}>
                      {tab.label}
                    </Typography>
                  ) : (
                    tab.label
                  )}
                  {tab.counter != null && <Chip label={String(tab.counter)} sx={styles.chip} />}
                </Box>
              }
              id={`tab-${index}`}
              aria-controls={`tabpanel-${index}`}
              {...tabProps}
              {...tab.tabProps}
            />
          ))}
        </Tabs>
      </Box>

      <Box sx={styles.contentContainer} className="tabpanel-content">
        {tabs?.map((tab, index) => (
          <Box
            key={index}
            hidden={activeTab !== index}
            role="tabpanel"
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
            {...tabPanelProps}
            {...tab.tabPanelProps}
          >
            {activeTab === index && tab.content}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

Tabpanel.displayName = 'Tabpanel';
